import { z } from 'zod';
import { US_STATE_CODES } from '../enums';
import type { PlanType, UnitStatus } from '../enums';
import {
  CONNECTION_TYPES,
  CUSTOMER_LANGUAGES,
  CUSTOMER_STATUSES,
  DEFAULT_NOTE_PRIORITY,
  ELIGIBLE_PROGRAMS,
  NLAD_EXCEPTION_CODES,
  NOTE_PRIORITIES,
  SUBSIDY_ADDRESS_TYPES,
  SUBSIDY_ENROLLMENT_TYPES,
} from '../enums.customers';
import type {
  CustomerStatus,
  EnrollmentSource,
  MailingType,
  SubsidyEnrollmentType,
} from '../enums.customers';
import { cardPaymentSchema, linePaymentLabel } from './billing';
import type { PlanPurchaseMode } from './billing';
import { digits, listQuerySchema, objectId, optionalText, queryBool } from './common';
import { zip5 } from './configuration';
import { imeiNumber, simNumber } from './inventory';
import type { UserRef } from './users';

/**
 * Customer Service (reference PC400): search, profile, notes, account status, address,
 * customer information and SIM change. Layout and labels follow docs/product-inventory.md,
 * appendix parts 7 and 8. One customer record is one line; a multi-line account would be a
 * parent with child customers (not built).
 */

const blankToUndefined = (v: unknown) => (typeof v === 'string' && v.trim() === '' ? undefined : v);

/* ---------------------------------- Search -------------------------------- */

/** The ten Advanced Search fields plus `q` for the header box. Matching is exact, as in the reference. */
export const CUSTOMER_SEARCH_FIELDS = [
  ['phone', 'Phone Number'],
  ['accountNo', 'Account Number'],
  ['enrollmentId', 'Enroll ID'],
  ['firstName', 'First Name'],
  ['lastName', 'Last Name'],
  ['email', 'Email Address'],
  ['sim', 'SIM'],
  ['imei', 'IMEI'],
  ['tag', 'Tag #'],
  ['alternateContact', 'Alternate Contact'],
] as const;
export type CustomerSearchField = (typeof CUSTOMER_SEARCH_FIELDS)[number][0];

/**
 * Account Type filter of the main lists (owner, 2026-11-04): Prepaid, Postpaid and Lifeline follow the
 * customer's plan type; Charity is the customer's own switch, whatever the plan.
 */
export const ACCOUNT_TYPE_FILTERS = ['Prepaid', 'Postpaid', 'Lifeline', 'Charity'] as const;
export type AccountTypeFilter = (typeof ACCOUNT_TYPE_FILTERS)[number];
const PLAN_TYPES_OF: Record<Exclude<AccountTypeFilter, 'Charity'>, string[]> = {
  Prepaid: ['Prepaid Plan'],
  Postpaid: ['Postpaid Plan'],
  Lifeline: ['LifeLine', 'ACP Plan', 'Combo Plan (Lifeline + ACP)'],
};
/** The customer condition of an Account Type; empty when none is chosen or the text is unknown. */
export function accountTypeCondition(type: string | undefined): Record<string, unknown> {
  if (type === 'Charity') return { 'flags.charity': true };
  const types = PLAN_TYPES_OF[type as Exclude<AccountTypeFilter, 'Charity'>];
  return types ? { accountType: { $in: types } } : {};
}
const accountTypeField = z
  .enum(ACCOUNT_TYPE_FILTERS)
  .optional()
  .or(z.literal('').transform(() => undefined));

export const customerSearchQuerySchema = listQuerySchema.extend({
  /** Prepaid, Postpaid, Lifeline or Charity (switch `account-type-filters`). */
  accountType: accountTypeField,
  /** Header "Search Customer": customer ID, phone number, enrollment ID or SIM (assumption). */
  q: optionalText(40),
  phone: optionalText(10),
  accountNo: optionalText(12),
  enrollmentId: optionalText(20),
  firstName: optionalText(30),
  lastName: optionalText(30),
  email: optionalText(120),
  sim: optionalText(25),
  imei: optionalText(25),
  tag: optionalText(40),
  alternateContact: optionalText(10),
});
export type CustomerSearchQuery = z.input<typeof customerSearchQuerySchema>;

/** Row of the search results and of "CSR Search History". */
export interface CustomerListItemDto {
  id: string;
  customerId: number;
  accountNo: string;
  enrollmentId: string;
  firstName: string;
  lastName: string;
  serviceAddress: string;
  city: string;
  state: string;
  zip: string;
  sim: string;
  mdn: string;
  carrier: string;
  isEsim: boolean;
  enrollmentType: PlanType | '';
  status: CustomerStatus;
  /** Set on Recent Searches rows: when this user last opened the customer. */
  viewedAt?: string;
}

export const recentSearchQuerySchema = listQuerySchema.extend({
  dateFrom: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  dateTo: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
});

/* ---------------------------------- Profile ------------------------------- */

export interface CustomerAddress {
  line1: string;
  line2: string;
  city: string;
  state: string;
  zip: string;
}

/** One authorized user of the Customer Information form. */
export interface AuthorizedUserDto {
  name: string;
  relationship: string;
  dob: string | null;
  ssnLast4: string | null;
  /** "A/U Password": a word the person says to be recognised; not a sign-in. */
  password: string;
  secretQuestion: string;
  secretAnswer: string;
}

export interface CustomerDto {
  id: string;
  customerId: number;
  accountNo: string;
  enrollmentId: string;
  firstName: string;
  middleName: string;
  lastName: string;
  status: CustomerStatus;
  /** Reason recorded with the last suspension or disconnection. */
  statusReason: string | null;
  language: (typeof CUSTOMER_LANGUAGES)[number];
  email: string;
  contactNumber: string;
  alternateNumber: string;
  serviceAddress: CustomerAddress;
  mailingAddress: CustomerAddress & { type: MailingType };
  /** The four contact switches of the profile (PC805). */
  preferences: {
    stopPromoSms: boolean;
    stopPromoEmail: boolean;
    stopTxnSms: boolean;
    stopTxnEmail: boolean;
  };
  tags: string[];
  /**
   * "Customer SSN" and "Customer DOB" (PC253): the last four digits and a calendar date.
   * Both are null when empty and when the caller lacks PC253 (`visible` tells which).
   */
  identity: { ssnLast4: string | null; dob: string | null; visible: boolean };
  secretQuestion: string;
  secretAnswer: string;
  subscriberId: string;
  /** "Authorized User1": someone else who may speak for the account. */
  authorizedUser: AuthorizedUserDto;
  /** "Authorize User2" of the reference form. */
  authorizedUser2: AuthorizedUserDto;
  /** "PIN": the IVR PIN (changed with "Update IVR PIN"); '' when none is set. */
  pin: string;
  /** "DID No" (changed with "Update DID"); '' when none is set. */
  didNumber: string;
  /** "Company": the company the account belongs to. */
  company: string;
  /** "Current Balance": what is still owed on unpaid invoices. */
  currentBalanceCents: number;
  /** The switches Disable Credit Card (PC810), Do not sell info (PC846), Delete Data (PC847). */
  flags: CustomerFlags;
  /** "Portout Lock": the number may not be ported out while this is on. */
  portoutLock: boolean;
  /**
   * Customer Classification, Collection Protection Status and Suppress Bills (release switch
   * `customer-account-dialogs`).
   */
  accountFlags?: {
    classificationId: string | null;
    classification: string;
    collectionProtection: boolean;
    suppressBills: boolean;
    /** "Collection Status" (switch `collection-status`). */
    collectionStatus: string;
  };
  /** "Current Shipping Info": the customer's latest shipment order; null when the order was not shipped to them. */
  shipping: {
    status: string;
    shipper: string;
    trackingNumber: string;
    shippedAt: string | null;
  } | null;
  /** "Line Information". */
  line: {
    mdn: string | null;
    sim: string | null;
    isEsim: boolean;
    imei: string | null;
    makeModel: string | null;
    /** The device is a unit of the inventory (changed with Change Device ID, not typed in). */
    deviceFromStock: boolean;
    /** Wifi Calling is switched on for the line (Features and Services). */
    wifiCalling: boolean;
    accountType: PlanType | '';
    carrier: { id: string; code: string; name: string } | null;
    plan: { id: string; name: string; priceCents: number } | null;
    planExpiresAt: string | null;
    /** "Ported MDN": the number came from another provider (a completed port-in). */
    portedMdn: boolean;
    /** "Plan Details", built from the allowances of the plan's carrier code. */
    planDetails: string;
    /** "Activation Code/QR-code": the eSIM profile's download address, kept from the last E-Sim Change. */
    esimActivationCode: string | null;
  };
  /** "Other Information". */
  other: {
    walletBalanceCents: number;
    orderBy: UserRef | null;
    masterAgent: UserRef | null;
    source: EnrollmentSource;
    activatedAt: string | null;
    disconnectedAt: string | null;
    /** "Approved by" and "Application Approval": the fraud review decision, when the order was held. */
    approvedBy: UserRef | null;
    applicationApprovalAt: string | null;
    /** "Non-Lifeline Activation": when a prepaid or postpaid line was activated. */
    nonLifelineActivationAt: string | null;
    /** Auto Pay is on (the profile offers "Cancel AutoPay"). */
    autopay: boolean;
    /** "Suspended": when the line was last suspended. */
    suspendedAt: string | null;
    /** The plan expiry rows of "Other Information". */
    expiry: {
      checksAutopay: boolean;
      checksWallet: boolean;
      planExpiredMoveTo: string;
      graceExpiresAt: string | null;
      graceExpiredMoveTo: string;
    };
  };
  /** Status changes the caller may make from the current status. */
  allowedStatusChanges: Array<{
    to: CustomerStatus;
    label: string;
    code: string;
    reason: 'disconnectReason' | 'suspendReason' | null;
  }>;
  createdAt: string;
}

/** "Portout Lock" dialog: lock or unlock the number against port-out. */
export const portoutLockSchema = z.object({ locked: z.boolean() });

/* ---- Customer page dialogs of the walk of 2026-10-23 (switch `customer-account-dialogs`) ---- */

/** "Update plan custom expiry date": Select Date. */
export const planCustomExpirySchema = z.object({
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Select Date'),
});
/** "Update Customer Classification". */
export const customerClassificationSchema = z.object({
  classificationId: z
    .string({ required_error: 'Please select a classification option.' })
    .regex(/^[a-f\d]{24}$/i, 'Please select a classification option.'),
});
export const COLLECTION_PROTECTION_STATUSES = ['Enable', 'Disable'] as const;
/** "Change Collection Protection Status To". */
export const collectionProtectionSchema = z.object({
  status: z.enum(COLLECTION_PROTECTION_STATUSES, {
    errorMap: () => ({ message: 'Please Select Collection Protection Status' }),
  }),
});
/**
 * "Collection Status" (switch `collection-status`). Assumption: the reference's choices were not
 * seen; these follow its "Collections - ..." note types, with None for an account not in
 * collections. Stored and shown; nothing else reads it.
 */
export const COLLECTION_STATUSES = [
  'None',
  'Full Payment',
  'Partial Payment',
  'Payment Arrangements',
  'No Answer',
  'No Action Taken',
] as const;
export const collectionStatusSchema = z.object({
  status: z.enum(COLLECTION_STATUSES, {
    errorMap: () => ({ message: 'Please Select Collection Status' }),
  }),
});
export const SUPPRESS_BILLS_VALUES = ['YES', 'NO'] as const;
/** "Suppress Bills To". */
export const suppressBillsSchema = z.object({
  value: z.enum(SUPPRESS_BILLS_VALUES, {
    errorMap: () => ({ message: 'Please Select Suppress Bills' }),
  }),
});
/** The winback notification goes to the line's number or to the alternate number. */
export const WINBACK_TARGETS = ['MDN', 'ALTERNATE'] as const;
export const winbackSchema = z.object({
  to: z.enum(WINBACK_TARGETS, { errorMap: () => ({ message: 'Choose the mobile number' }) }),
});
export type PortoutLockInput = z.input<typeof portoutLockSchema>;

/** "Retention Note" dialog: what was said to keep the customer. */
export const retentionNoteSchema = z.object({
  text: z.string().trim().min(1, 'Enter the retention note').max(1000),
});
export type RetentionNoteInput = z.input<typeof retentionNoteSchema>;

/** The note type a retention note is listed under. */
export const RETENTION_NOTE_TYPE = 'Retention Note';

/** "Highest Priority Notes": the new priority of a note. */
export const notePrioritySchema = z.object({
  priority: z.enum(NOTE_PRIORITIES, { errorMap: () => ({ message: 'Select Priority' }) }),
});
export type NotePriorityInput = z.input<typeof notePrioritySchema>;

export interface CustomerFlags {
  /** 212Ez's own (owner, 2026-11-04): a customer served through a charity, whatever the plan. */
  charity: boolean;
  disableCreditCard: boolean;
  doNotSellInfo: boolean;
  deleteData: boolean;
}

/** The permission of each switch, as the reference profile shows it. */
export const CUSTOMER_FLAG_CODES: Record<keyof CustomerFlags, string> = {
  disableCreditCard: 'PC810',
  doNotSellInfo: 'PC846',
  deleteData: 'PC847',
  // Assumption: the account's classing, so Change Account Type's permission.
  charity: 'PC648',
};

/** Moves one or more of the switches; a switch left out keeps its value. */
export const customerFlagsSchema = z
  .object({
    disableCreditCard: z.boolean().optional(),
    doNotSellInfo: z.boolean().optional(),
    deleteData: z.boolean().optional(),
    charity: z.boolean().optional(),
  })
  .refine((v) => Object.values(v).some((x) => x !== undefined), 'Choose a switch to change');
export type CustomerFlagsInput = z.input<typeof customerFlagsSchema>;

/** "DID Number" dialog: a 10-digit number, or empty to clear it. Assumption: the format was not seen. */
export const didNumberSchema = z.object({
  didNumber: z
    .string()
    .trim()
    .regex(/^(\d{10})?$/, 'DID Number must be 10 digits'),
});
export type DidNumberInput = z.input<typeof didNumberSchema>;

/** "Update IVR PIN": "IVR PIN (4 digits)". */
export const ivrPinSchema = z.object({
  pin: z
    .string()
    .trim()
    .regex(/^\d{4}$/, 'IVR PIN must be 4 digits'),
});
export type IvrPinInput = z.input<typeof ivrPinSchema>;

/* ----------------------------------- Notes -------------------------------- */

export const noteCreateSchema = z.object({
  noteTypeId: z.string().regex(/^[a-f\d]{24}$/i, 'Select Note Type'),
  callerId: z.preprocess(blankToUndefined, digits(10, 10, 'Caller ID').optional()),
  text: z.string().trim().min(1, 'Enter the note').max(1000),
  priority: z.enum(NOTE_PRIORITIES).default(DEFAULT_NOTE_PRIORITY),
});
export type NoteCreateInput = z.input<typeof noteCreateSchema>;

export interface NoteDto {
  id: string;
  noteType: string;
  callerId: string;
  text: string;
  priority: (typeof NOTE_PRIORITIES)[number];
  createdBy: UserRef | null;
  createdAt: string;
  /** A voided note (Mark Void, PC248) is archived: it leaves the notes list for the archive page. */
  voided: boolean;
}

/**
 * "Display Notes" filters (the reference's Customer Notes dialog): the note's words, who posted
 * it and its priority. Its fourth filter, Ticket Status, is not offered: notes hang on no ticket
 * here. `archived` lists the voided notes ("View Archive Notes", an assumption: the reference's
 * rule for what is archived was not seen).
 */
export const noteListQuerySchema = listQuerySchema.extend({
  q: optionalText(200),
  postedBy: z.preprocess(blankToUndefined, objectId.optional()),
  priority: z.preprocess(blankToUndefined, z.enum(NOTE_PRIORITIES).optional()),
  archived: queryBool,
});
export type NoteListQuery = z.input<typeof noteListQuerySchema>;

/** The Audit Log tab's "Search:" box looks in the description, the action and the source. */
export const activityListQuerySchema = listQuerySchema.extend({ q: optionalText(200) });

/** Verify Email: the reference's own message after the mail was sent. */
export const VERIFY_EMAIL_SENT =
  "A verification email has been sent to the customer's email address.";

/* ----------------------- Change Account Type (PC648) ---------------------- */

/** The reference's own messages on "CSR Screen For Change Account Type". */
export const ACCOUNT_NOT_ACTIVE = 'Account is not Active, hence can not be loaded.';
export const NLAD_VERIFICATION_FAILED = 'NLAD verification was failed for this customer.';

const isOneOf = (list: readonly string[], value: string): boolean => list.includes(value);

/** "MM-DD-YYYY" (the reference's DOB format) as YYYY-MM-DD when it is a real date. */
export function isoFromUsDate(value: string): string | undefined {
  const m = /^(\d{2})-(\d{2})-(\d{4})$/.exec(value.trim());
  if (!m) return undefined;
  const iso = `${m[3]}-${m[1]}-${m[2]}`;
  const date = new Date(`${iso}T00:00:00Z`);
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === iso ? iso : undefined;
}

/**
 * The form of Change Account Type. Every rule is checked in one pass, so one answer names
 * every field to fix. Assumptions (the reference's required marks show only on the household
 * size): Address Type, SSN, DOB, Enrollment Type and Plan are required, NLAD needs the SSN and
 * the date of birth, and eligibility is shown by a program or by the income option.
 */
export const accountTypeChangeSchema = z
  .object({
    addressType: z.string().default(''),
    ssnLast4: z.string().trim().default(''),
    dob: z.string().trim().max(10).default(''),
    enrollmentType: z.string().default(''),
    planId: z.string().default(''),
    eligiblePrograms: z.array(z.enum(ELIGIBLE_PROGRAMS)).max(ELIGIBLE_PROGRAMS.length).default([]),
    incomeBased: z.boolean().default(false),
    householdSize: z.string().trim().max(3).default(''),
    bypassNlad: z.boolean().default(false),
    nladExceptionCode: z.string().default(''),
  })
  .superRefine((v, ctx) => {
    const fail = (path: string, message: string) =>
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: [path], message });
    if (!isOneOf(SUBSIDY_ADDRESS_TYPES, v.addressType)) fail('addressType', 'Select Address Type');
    if (!/^\d{4}$/.test(v.ssnLast4)) fail('ssnLast4', 'Enter the last 4 digits of the SSN');
    const dob = isoFromUsDate(v.dob);
    if (!dob) fail('dob', 'Enter the date of birth as MM-DD-YYYY');
    else if (dob >= new Date().toISOString().slice(0, 10))
      fail('dob', 'The date of birth must be in the past');
    if (!isOneOf(SUBSIDY_ENROLLMENT_TYPES, v.enrollmentType))
      fail('enrollmentType', 'Select Enrollment Type');
    if (!/^[a-f\d]{24}$/i.test(v.planId)) fail('planId', 'Select Plan');
    if (!v.eligiblePrograms.length && !v.incomeBased)
      fail('eligiblePrograms', 'Select an eligible program, or the income option');
    if (v.incomeBased && !(/^\d{1,3}$/.test(v.householdSize) && Number(v.householdSize) > 0))
      fail('householdSize', 'Enter how many people are in the household');
    if (v.bypassNlad && !isOneOf(NLAD_EXCEPTION_CODES, v.nladExceptionCode))
      fail('nladExceptionCode', 'Select NLAD Exception Code');
  });
export type AccountTypeChangeInput = z.input<typeof accountTypeChangeSchema>;
export type AccountTypeChangeData = z.output<typeof accountTypeChangeSchema>;

export interface AccountTypeScreenDto {
  /** '' when the screen can be used; otherwise the reference's message (ACCOUNT_NOT_ACTIVE). */
  blocked: string;
  firstName: string;
  lastName: string;
  address: string;
  phone: string;
  /** Plans offered for each enrollment type; empty when the screen is blocked. */
  plans: Record<SubsidyEnrollmentType, Array<{ id: string; name: string; priceCents: number }>>;
}

export interface AccountTypeChangeResultDto {
  message: string;
  accountType: PlanType;
  planName: string;
}

/* --------------------------- Add Product (PC945) -------------------------- */

export interface AddProductResultDto {
  message: string;
}

/* ------------------------ Update Account Status (PC95) -------------------- */

export const statusChangeSchema = z.object({
  status: z.enum(CUSTOMER_STATUSES, { required_error: 'Select New Status' }),
  connectionType: z.enum(CONNECTION_TYPES, { required_error: 'Select Connection Type' }),
  /** Disconnection or suspend reason; required when the transition says so. */
  reasonId: objectId.nullable().default(null),
});
export type StatusChangeInput = z.input<typeof statusChangeSchema>;

/* ------------------------------ Modify (PC401) ---------------------------- */

/** Change Address (PC100). */
export const addressUpdateSchema = z.object({
  scope: z.enum(['service', 'mailing', 'both']),
  line1: z.string().trim().min(1, 'Address1 is required').max(120),
  line2: z.string().trim().max(120).optional().default(''),
  city: z.string().trim().min(1, 'City is required').max(60),
  state: z.enum(US_STATE_CODES, { required_error: 'State is required' }),
  zip: zip5,
});
export type AddressUpdateInput = z.input<typeof addressUpdateSchema>;

/** The last four digits of an SSN; empty clears it. Never more than four digits. */
const ssnLast4 = z
  .string()
  .trim()
  .regex(/^(\d{4})?$/, 'Enter the last 4 digits of the SSN')
  .optional();
/** A date of birth as YYYY-MM-DD; empty clears it. */
const birthDate = z
  .string()
  .trim()
  .regex(/^(\d{4}-\d{2}-\d{2})?$/, 'Enter a valid date')
  .refine(
    (v) => {
      if (!v) return true;
      const at = new Date(`${v}T00:00:00Z`);
      return !Number.isNaN(at.getTime()) && at.toISOString().slice(0, 10) === v && at <= new Date();
    },
    { message: 'Enter a date of birth that is not in the future' },
  )
  .optional();
const infoText = (max: number) => z.string().trim().max(max).optional();

/** The five questions the reference offers for "Customer Security Question" and "A/U S/Q". */
export const SECURITY_QUESTIONS = [
  'What is the name of the hospital in which you were born?',
  "What was your favorite school teacher's name?",
  "What was your first pet's name?",
  'What was your favorite subject in school?',
  "What is your father's middle name?",
] as const;

const authorizedUserInput = z
  .object({
    name: infoText(60),
    relationship: infoText(20),
    dob: birthDate,
    ssnLast4,
    password: infoText(30),
    secretQuestion: infoText(100),
    secretAnswer: infoText(100),
  })
  .optional();

/**
 * Customer Information (PC377), with the fields of the reference form (appendix 13.5):
 * language, names, SSN last 4, DOB, alternate phone, security question and answer, email,
 * subscriber ID and two authorized users. The customer's own Password is left out (it is
 * disabled on the reference too, and a customer has no sign-in here). The fields after
 * `alternateNumber` are left as they are when they are not sent, and cleared when sent
 * empty; SSN and DOB also need PC253.
 */
export const customerInfoSchema = z.object({
  language: z.enum(CUSTOMER_LANGUAGES).default('English'),
  firstName: z.string().trim().min(1, 'First Name is required').max(30),
  middleName: z.string().trim().max(30).optional().default(''),
  lastName: z.string().trim().min(1, 'Last Name is required').max(30),
  email: z.string().trim().max(120).email('Enter valid email').or(z.literal('')).default(''),
  contactNumber: z.preprocess(blankToUndefined, digits(10, 10, 'Phone Number').optional()),
  alternateNumber: z.preprocess(blankToUndefined, digits(10, 10, 'Alternate Ph').optional()),
  ssnLast4,
  dob: birthDate,
  secretQuestion: infoText(100),
  secretAnswer: infoText(100),
  subscriberId: infoText(30),
  authorizedUser: authorizedUserInput,
  authorizedUser2: authorizedUserInput,
});
export type CustomerInfoInput = z.input<typeof customerInfoSchema>;

/**
 * Tag (PC759): "Tag #", at most 50 characters, with "Add"; a customer that has a tag shows
 * "Delete" instead (appendix 13.5). So a customer has one tag or none: the list holds at
 * most one entry. Advanced Search finds a customer by it.
 */
export const customerTagsSchema = z.object({
  tags: z
    .array(z.string().trim().min(1, 'Enter Tag #').max(50, 'Tag # has at most 50 characters'))
    .max(1, 'A customer has one tag. Delete it to add another.'),
});
export type CustomerTagsInput = z.input<typeof customerTagsSchema>;

/** The contact switches on the profile (PC805). */
export const preferencesSchema = z.object({
  stopPromoSms: z.boolean(),
  stopPromoEmail: z.boolean(),
  stopTxnSms: z.boolean(),
  stopTxnEmail: z.boolean(),
});

/** ESN/SIM Change (PC18). */
export const simChangeSchema = z.object({
  newSim: simNumber,
  /** An Active change reason whose process type is CHANGE ESN or BOTH. */
  reasonId: z.string().regex(/^[a-f\d]{24}$/i, 'Select Reason'),
  connectionType: z.enum(CONNECTION_TYPES, { required_error: 'Select Type' }),
  /** "Who got it back (Employee Login ID)": the user the old SIM was handed back to, if any. */
  returnedTo: z.string().trim().max(40).optional().default(''),
  /** "Reason for Bad ESN/SIM": what is wrong with an old SIM a reason marks Bad. */
  badReason: z.string().trim().max(100).optional().default(''),
});
export type SimChangeInput = z.input<typeof simChangeSchema>;

/** Answer of ESN/SIM Change: the customer after the change and what became of the SIM taken back. */
export interface SimChangeResultDto {
  customer: CustomerDto;
  oldSim: string;
  /** Status the old SIM now has in inventory; null when it was not an inventory unit. */
  oldSimStatus: UnitStatus | null;
}

/* ----------------------- Activity log and order history ------------------- */

/** Row of Customer Activity Log (PC770): Added By, Description, Date Time, Action, Source. */
export interface ActivityDto {
  id: string;
  description: string;
  action: string;
  source: string;
  by: UserRef | null;
  at: string;
}

/** Row of Order History (PC76): Stage, Order By, Order DateTime, Status. */
export interface CustomerOrderDto {
  enrollmentId: string;
  stage: string;
  orderBy: UserRef | null;
  orderedAt: string;
  status: string;
}

/* ------------------------ Manage Saved Card (PC900) ------------------------ */

/**
 * Types offered for a saved card. Assumption: the reference's form has a "Type of Card"
 * field whose options were not read; these are the four common card schemes.
 */
export const SAVED_CARD_TYPES = ['VISA', 'MasterCard', 'American Express', 'Discover'] as const;

/**
 * "Add New Card" / "Update Card Details". No payment gateway is connected, so the card
 * number and the security code of the reference's form are deliberately not asked for and
 * never stored: a saved card here is a description of the card (last four digits, type,
 * expiry, name and billing address) and cannot be charged.
 */
export const savedCardSchema = z.object({
  nameOnCard: z.string().trim().min(1, 'Name on Card is required').max(60),
  cardType: z.enum(SAVED_CARD_TYPES, { required_error: 'Select Type of Card' }),
  last4: z
    .string()
    .trim()
    .regex(/^\d{4}$/, 'Last 4 Digit must be 4 digits'),
  expMonth: z.coerce
    .number({ invalid_type_error: 'Select Expiry Month' })
    .int()
    .min(1, 'Select Expiry Month')
    .max(12, 'Select Expiry Month'),
  expYear: z.coerce
    .number({ invalid_type_error: 'Select Expiry Year' })
    .int()
    .min(2000, 'Select Expiry Year')
    .max(2100, 'Select Expiry Year'),
  line1: z.string().trim().min(1, 'Address 1 is required').max(120),
  line2: z.string().trim().max(120).optional().default(''),
  city: z.string().trim().min(1, 'City is required').max(60),
  state: z.enum(US_STATE_CODES, { required_error: 'State is required' }),
  zip: zip5,
  isPrimary: z.boolean().default(false),
});
export type SavedCardInput = z.input<typeof savedCardSchema>;

/** "Save card" through the payment gateway's own form (PC777, switch card-payments). */
export const gatewayCardSchema = z.object({
  // The token of the gateway's form; the pattern is CARD_TOKEN of billing.ts.
  paymentMethodId: z
    .string()
    .regex(
      /^(pm_[A-Za-z0-9_]{3,255}|opaque:[A-Za-z0-9._-]{3,80}:[A-Za-z0-9+/=]{3,8000})$/,
      'Enter the card',
    ),
  nameOnCard: z.string().trim().max(60).optional().default(''),
  isPrimary: z.boolean().optional().default(false),
});
export type GatewayCardInput = z.input<typeof gatewayCardSchema>;

export interface SavedCardDto {
  id: string;
  nameOnCard: string;
  cardType: (typeof SAVED_CARD_TYPES)[number];
  last4: string;
  expMonth: number;
  expYear: number;
  /** The expiry month has passed. */
  expired: boolean;
  isPrimary: boolean;
  address: { line1: string; line2: string; city: string; state: string; zip: string };
  /** Saved through the payment gateway, so it can be charged (switch card-payments). */
  chargeable: boolean;
}

/* ------------------- Line changes from the Quick Links menu ----------------- */

/** Reasons a device change may use: those of the process CHANGE DEVICE or BOTH. */
export const DEVICE_CHANGE_PROCESS_TYPES = ['CHANGE DEVICE', 'BOTH'] as const;

/**
 * Change Mobile number (PC90): Old MDN (read-only), ZIP Code*, Type of Change*. The
 * reference also offers "Non-Electronic Change"; its form has no field for the new number,
 * so only the electronic change, where the carrier issues the number, is offered here.
 */
export const NUMBER_CHANGE_TYPES = ['Electronic Change'] as const;
export const numberChangeSchema = z.object({
  zip: zip5,
  changeType: z.enum(NUMBER_CHANGE_TYPES, { required_error: 'Select Type of Change' }),
});
export type NumberChangeInput = z.input<typeof numberChangeSchema>;

/** Change Device ID (PC101): Old Device ID (read-only), New Device ID, reason. */
export const deviceChangeSchema = z.object({
  newDeviceId: imeiNumber,
  /** An Active change reason whose process type is CHANGE DEVICE or BOTH. */
  reasonId: z.string().regex(/^[a-f\d]{24}$/i, 'Select Reason'),
  /** "Who got it back (Employee Login ID)": the user the old device was handed back to, if any. */
  returnedTo: z.string().trim().max(40).optional().default(''),
  /** "Reason For Bad Device": what is wrong with an old device a reason marks Bad. */
  badReason: z.string().trim().max(100).optional().default(''),
});
export type DeviceChangeInput = z.input<typeof deviceChangeSchema>;

/**
 * The two top-up screens (appendix 13.17): "Add Minutes / Top-Up Balance" (Quick Link "Add
 * Topup - Minute/Text", PC60) and "CSR Screen For Adding Data" (Quick Link "Add Data Topup",
 * PC64). Assumption: a top-up with minutes or texts belongs to the first, one with data only
 * to the second.
 */
export const TOPUP_KINDS = ['minutes', 'data'] as const;
export type TopupKind = (typeof TOPUP_KINDS)[number];
export const TOPUP_KIND_META: Record<
  TopupKind,
  {
    title: string;
    code: string;
    path: string;
    heading: string;
    subtitle: string;
    walletBox: string;
    select: string;
    addLink: string;
    note: string;
  }
> = {
  minutes: {
    title: 'Add Topup - Minute/Text',
    code: 'PC60',
    path: 'topup',
    heading: 'Add Minutes / Top-Up Balance',
    subtitle: 'Purchase additional minutes or add balance to your account',
    walletBox: 'Use wallet balance for this purchase',
    select: 'Select',
    addLink: 'Add New Topup Plan (PC232)',
    note: 'The purchased amount will be added to your account balance immediately. Charges will appear on your next billing statement.',
  },
  data: {
    title: 'Add Data Topup',
    code: 'PC64',
    path: 'data-topup',
    heading: 'CSR Screen For Adding Data',
    subtitle: 'Purchase additional data or add balance to your account',
    walletBox: 'Apply wallet balance to this transaction',
    select: 'Select Data Topup',
    addLink: 'Add New Data Topup Plan (PC232)',
    note: 'This amount will be charged on your next billing cycle.',
  },
};

/** The ways of paying of the two screens, as their tabs show them (Free Topup: PC419). */
export const TOPUP_METHODS = ['cash', 'moneyOrder', 'moneyGram', 'wallet', 'free', 'card'] as const;
export type TopupMethod = (typeof TOPUP_METHODS)[number];
export const TOPUP_PAYMENT_CODES: Record<TopupKind, Record<TopupMethod, string>> = {
  minutes: {
    cash: 'PC415',
    moneyOrder: 'PC416',
    moneyGram: 'PC417',
    wallet: 'PC418',
    free: 'PC419',
    card: 'PC414',
  },
  // Assumption: the reference's data top-up card code was not seen; Add Minutes CC is used.
  data: {
    cash: 'PC421',
    moneyOrder: 'PC422',
    moneyGram: 'PC423',
    wallet: 'PC424',
    free: 'PC419',
    card: 'PC414',
  },
};
export const topupMethodLabel = (m: string): string =>
  m === 'free' ? 'Free Topup' : linePaymentLabel(m);

export const topupPurchaseSchema = z.object({
  topupId: z.string().regex(/^[a-f\d]{24}$/i, 'Select Topup'),
  /** Cash, money order, MoneyGram, the customer's wallet, Free Topup or a card. */
  method: z.enum(TOPUP_METHODS).default('cash'),
  card: cardPaymentSchema.optional(),
  receiptNumber: z.string().trim().max(30).optional().default(''),
  /** "Use wallet balance for this purchase": the wallet pays what it can, the method the rest. */
  useWallet: z.preprocess((v) => v === true || v === 'true', z.boolean()).default(false),
});
export type TopupPurchaseInput = z.input<typeof topupPurchaseSchema>;

export interface TopupOptionDto {
  id: string;
  name: string;
  /** Shown in "price - code / name"; the top-up's wholesale code (our reading of "code"). */
  code: string;
  costCents: number;
  voiceMinutes: number;
  textCount: number;
  dataMb: number;
}

/** Top-ups a line has received, newest first. */
export interface TopupPurchaseDto {
  id: string;
  name: string;
  costCents: number;
  source: string;
  at: string;
  by: UserRef | null;
}

export const PLAN_REQUEST_STATUSES = ['Pending', 'Completed', 'Cancelled', 'Failed'] as const;

/**
 * Row of the reference's "Advance Plan List": #, Customer Id, Old Plan, New Plan, Plan
 * Effective Date, Plan Change Mode, Action. A renewal is a request whose new plan is the old.
 */
export interface PlanChangeRequestDto {
  id: string;
  kind: 'Renew' | 'Change';
  customerId: number;
  oldPlan: string;
  newPlan: string;
  effectiveDate: string;
  mode: PlanPurchaseMode;
  /** The invoice of the money taken when the request was made. */
  invoiceNo: string;
  amountCents: number;
  status: (typeof PLAN_REQUEST_STATUSES)[number];
  /** Why a request failed. */
  message: string;
  createdAt: string;
  createdBy: UserRef | null;
}

/* ---------------- Edit Device Info tab and Features tab (appendix 13.5) ----- */

/**
 * The reference's Modify page words the two connection types differently from the status
 * screen: "Electronic Change" and "Non- Electronic Change" (spelt so).
 */
export const CHANGE_TYPE_LABELS: Record<(typeof CONNECTION_TYPES)[number], string> = {
  Electronically: 'Electronic Change',
  'Non-Electronically': 'Non- Electronic Change',
};

/**
 * The three forms behind "Type of ESN/SIM Change" in the ESN/SIM block of the reference.
 * Change Carrier and Change UICCID are modes of that block, each with its own permission.
 */
export const SIM_CHANGE_KINDS = [
  { key: 'sim', label: 'ESN/SIM Change', code: 'PC18' },
  { key: 'carrier', label: 'Change Carrier', code: 'PC530' },
  { key: 'uiccid', label: 'Change UICCID', code: 'PC646' },
] as const;
export type SimChangeKind = (typeof SIM_CHANGE_KINDS)[number]['key'];

/** Change UICCID (PC646): the reference's three reasons, and what becomes of the old card. */
export const UICCID_CHANGE_REASONS = ['Bad UICCID', 'Lost/Stolen', 'Courtesy Change'] as const;
export const UICCID_REASON_STATUS: Record<(typeof UICCID_CHANGE_REASONS)[number], UnitStatus> = {
  'Bad UICCID': 'Bad',
  'Lost/Stolen': 'Lost/Stolen',
  'Courtesy Change': 'Free',
};
/**
 * The line moves to another SIM card of the same carrier; the number stays. Fields of the
 * reference: New UICCID, Reason for Change UICCID, Reason for Bad UICCID (asked when the
 * reason is "Bad UICCID"; the server checks that), Type of Change.
 */
export const uiccidChangeSchema = z.object({
  newSim: simNumber,
  reason: z.enum(UICCID_CHANGE_REASONS, { errorMap: () => ({ message: 'Select Reason' }) }),
  badReason: z.string().trim().max(100).optional().default(''),
  // An unanswered select arrives as an empty text, which is not "missing" to zod.
  connectionType: z.enum(CONNECTION_TYPES, { errorMap: () => ({ message: 'Select Type' }) }),
});

/**
 * E-Sim Change (PC1078). The reference's form has the number and "Submit" and nothing
 * else: the carrier issues a new eSIM profile for the line, and the screen then shows the
 * QR code to install it. So there is nothing to send; this is what comes back.
 */
export interface EsimChangeResultDto {
  customer: CustomerDto;
  /** The number of the new eSIM profile. */
  iccid: string;
  /** What the QR code holds: the address the phone downloads the profile from. */
  activationCode: string;
}
/** The reference's words under the QR code. */
export const ESIM_INSTRUCTIONS = [
  "you'll need to install your eSIM in order to activate your service. scan the QR code using your phone's camera and follow the prompts to complete installation.",
  'make sure your device is connected to wifi',
  'go to settings -> cellular -> add eSIM',
  "click 'use QR code' and scan this QR code",
] as const;

/** Change Carrier (PC530): the reference's two reasons, and what becomes of the old SIM. */
export const CARRIER_CHANGE_REASONS = ['Bad ESN/SIM', 'Courtesy Change'] as const;
export const CARRIER_REASON_STATUS: Record<(typeof CARRIER_CHANGE_REASONS)[number], UnitStatus> = {
  'Bad ESN/SIM': 'Bad',
  'Courtesy Change': 'Free',
};
/**
 * The line moves to another carrier. Fields of the reference: New ESN/SIM, Areacode,
 * Carrier, Plan ID, Reason for Change ESN/SIM, Reason for Bad ESN (asked for "Bad ESN/SIM";
 * the server checks that). The new carrier gives the new number ("New MDN" is read-only);
 * the old line is disconnected (setting SC140). "Model" of the reference is not built: a SIM of
 * the stock already has its model. "Who got it back" puts the old SIM in that user's stock.
 */
export const carrierChangeSchema = z.object({
  carrierId: z.string().regex(/^[a-f\d]{24}$/i, 'Select Carrier'),
  planId: z.string().regex(/^[a-f\d]{24}$/i, 'Select Plan ID'),
  newSim: simNumber,
  areaCode: z
    .string()
    .trim()
    .regex(/^(\d{3})?$/, 'Select Area Code')
    .optional()
    .default(''),
  reason: z.enum(CARRIER_CHANGE_REASONS, { errorMap: () => ({ message: 'Select Reason' }) }),
  badReason: z.string().trim().max(100).optional().default(''),
  /** "Who got it back(Employee LoginID)": the user the old SIM was handed back to, if any. */
  returnedTo: z.string().trim().max(40).optional().default(''),
});
/** Carriers the line can move to, each with its plans sold in the customer's state. */
export interface CarrierChangeOptionsDto {
  carriers: Array<{
    id: string;
    name: string;
    code: string;
    plans: Array<{ id: string; name: string; code: number; priceCents: number }>;
  }>;
  /** Area codes a new number can be asked for, shown as "206 (State-WA)". */
  areaCodes: Array<{ code: string; state: string }>;
}

/**
 * Features and Services (PC92). "Add WFC" asks for the address emergency calls over Wifi
 * are located at: Street 1, Street 2 and Zip; City and State follow from the Zip.
 */
export const wifiCallingSchema = z.object({
  enabled: z.boolean(),
  line1: z.string().trim().max(255).optional().default(''),
  line2: z.string().trim().max(255).optional().default(''),
  zip: z
    .string()
    .trim()
    .regex(/^(\d{5})?$/, 'Enter a 5 digit Zip')
    .optional()
    .default(''),
});
export interface LineFeaturesDto {
  /** What the carrier said about the line when the features were loaded. */
  lineStatus: string;
  features: Array<{ code: string; name: string; enabled: boolean; address: string }>;
}
/** Answer to "Query HLR", "Cancel Device" and "Reset Voice Mail Password". */
export interface LineRequestDto {
  /** The carrier's reference for the request. */
  reference: string;
  /** Present on Query HLR: the state of the line in the carrier's register. */
  lineStatus: string | null;
}

/** Usage (PC254): the reference's filters. */
export const USAGE_TYPES = ['ALL', 'DATA', 'SMS', 'VOICE'] as const;
const usageDay = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Enter a valid date')
  .optional();
export const usageQuerySchema = z.object({
  from: usageDay,
  to: usageDay,
  type: z.enum(USAGE_TYPES).default('ALL'),
  // The reference's "Record" choices end at 1000; "ALL" sends no limit.
  limit: z.coerce.number().int().min(1).max(1000).default(1000),
});
export interface UsageDto {
  carrier: string;
  /** Usage records as the carrier reports them. The columns are this project's. */
  records: Array<{ at: string; type: string; quantity: number; unit: string; detail: string }>;
  /** What is known here without the carrier: the plan and the top-ups added in the period. */
  plan: string;
  topups: Array<{ name: string; at: string }>;
}
